<template>
  <section class="pricing-section">
    <div class="container">
      <div class="pricing-layout">
        <div class="pricing-intro">
          <div class="section-tag">
            <i class="fas fa-times"></i>
            <span>{{ content.header.tag }}</span>
          </div>
          <h2>{{ content.header.title }}</h2>
          <p>{{ content.header.description }}</p>
          <div class="pricing-toggle">
            <button class="toggle-btn" :class="{ active: isMonthly }" @click="isMonthly = true">Monthly</button>
            <button class="toggle-btn" :class="{ active: !isMonthly }" @click="isMonthly = false">Yearly</button>
          </div>
        </div>
        
        <div class="pricing-plans">
          <div class="pricing-card" v-for="(plan, index) in content.plans" :key="index" :class="{ featured: plan.featured }">
            <div class="plan-header">
              <h3>{{ plan.name }}</h3>
              <div v-if="plan.discount" class="discount-tag">{{ plan.discount }}</div>
              <div v-if="plan.popular" class="popular-tag">{{ plan.popular }}</div>
            </div>
            <div class="plan-price">
              <span class="price">${{ isMonthly ? plan.price.monthly : plan.price.yearly }}</span>
              <span class="period">/{{ isMonthly ? 'month' : 'year' }}</span>
            </div>
            <p class="plan-description">{{ plan.description }}</p>
            <button class="plan-btn" :class="plan.buttonType">{{ plan.buttonText }} <i class="fas fa-arrow-right"></i></button>
            <div class="plan-features">
              <h4>Included:</h4>
              <ul>
                <li v-for="(feature, featureIndex) in plan.features" :key="featureIndex">
                  <i class="fas fa-check"></i> {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'AIMLItsPricingComp',
  props: {
    content: {
      type: Object,
      required: true
    }
  },
  setup() {
    const isMonthly = ref(true)

    return {
      isMonthly
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Pricing Section */
.pricing-section {
  padding: 100px 0;
  background: linear-gradient(145deg, #0a0f1c, #1a2332);
  color: #fff;
}

.pricing-layout {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 80px;
  align-items: start;
}

.pricing-intro {
  position: sticky;
  top: 100px;
}

.pricing-intro .section-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(251, 14, 118, 0.1);
  border: 1px solid rgba(251, 14, 118, 0.3);
  padding: 8px 20px;
  border-radius: 25px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #FB0E76;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.pricing-intro .section-tag i {
  font-size: 12px;
  color: #FB0E76;
}

.pricing-intro h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 20px 0;
  line-height: 1.2;
}

.pricing-intro p {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
}

.pricing-toggle {
  display: flex;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.toggle-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.toggle-btn.active {
  background: #FB0E76;
  color: #fff;
  box-shadow: 0 2px 8px rgba(251, 14, 118, 0.3);
  transform: translateY(-1px);
}

.toggle-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.pricing-plans {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.pricing-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 20px;
  padding: 40px;
  position: relative;
  transition: all 0.3s ease;
}

.pricing-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(246, 236, 79, 0.3);
}

.pricing-card.featured {
  border-color: rgba(251, 14, 118, 0.3);
  background: rgba(251, 14, 118, 0.05);
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.plan-header h3 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.discount-tag,
.popular-tag {
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.discount-tag {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.popular-tag {
  background: #FB0E76;
  color: #fff;
}

.plan-price {
  margin-bottom: 20px;
}

.plan-price .price {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.plan-price .period {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-left: 8px;
}

.plan-description {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 30px;
}

.plan-btn {
  width: 100%;
  padding: 15px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.plan-btn.outline {
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #FB0E76;
  color: #FB0E76;
}

.plan-btn.outline:hover {
  background: #FB0E76;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(251, 14, 118, 0.3);
}

.plan-btn.primary {
  background: #FB0E76;
  border: 1px solid #FB0E76;
  color: #fff;
}

.plan-btn.primary:hover {
  background: #FFD700;
  border-color: #FFD700;
  color: #000;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.plan-btn i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.plan-btn:hover i {
  transform: translateX(2px);
}

.plan-features h4 {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
}

.plan-features ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
  font-size: 14px;
}

.plan-features li i {
  color: #FB0E76;
  font-size: 12px;
}

/* Responsive Design */
@media only screen and (max-width: 1200px) {
  .pricing-layout {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  
  .pricing-intro {
    position: static;
  }
  
  .pricing-plans {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 991px) {
  .pricing-section {
    padding: 80px 0;
  }
  
  .section-header h2 {
    font-size: 36px;
  }
}

@media only screen and (max-width: 767px) {
  .section-header h2 {
    font-size: 28px;
  }
  
  .pricing-intro h2 {
    font-size: 36px;
  }
  
  .plan-price .price {
    font-size: 36px;
  }
}
</style>
